import type { CrudFilter } from '@refinedev/core'

/**
 * The Gallery's Memories / Group photos / Gallery split.
 *
 * A Classroom memory and a group photo are BOTH a photo with `group_id` set
 * (Classrooms Phase 1, `20261016120100_classrooms_memories.sql`; Groups
 * completion track b1-media, `20261017150000_groups_media_schema.sql`) —
 * `group_id` alone cannot tell them apart, only the linked group's `kind`
 * can (`class_chapter` vs `interest`). `photoKindFilters` therefore needs the
 * caller to resolve that split itself (`groupKindSplit` below) and hand back
 * the two id lists; every other photo has `group_id` null.
 *
 * `value: null` is LOAD-BEARING for 'nnull': @refinedev/supabase has no
 * dedicated case for it and falls through to
 * query.filter(field, 'not.is', value), so the value must be null to send
 * `group_id=not.is.null`. ('null' goes through query.is(field, null).)
 *
 * A SENTINEL UUID is used for an empty `in` list — @refinedev/supabase turns
 * `operator: 'in', value: []` into `group_id=in.()`, which PostgREST rejects
 * as a malformed list syntax rather than "match nothing". A UUID that can
 * never exist in `groups.id` (gen_random_uuid() never returns the nil UUID)
 * is a safe, well-understood way to ask for zero rows instead.
 */
export type PhotoKind = 'all' | 'memories' | 'group' | 'gallery'

export const PHOTO_KIND_OPTIONS: { label: string; value: PhotoKind }[] = [
  { label: 'All photos', value: 'all' },
  { label: 'Memories only', value: 'memories' },
  { label: 'Group photos', value: 'group' },
  { label: 'Gallery only', value: 'gallery' },
]

const NO_MATCH_SENTINEL = '00000000-0000-0000-0000-000000000000'

/** The two group-id lists `photoKindFilters` needs for 'memories'/'group'. */
export interface GroupKindSplit {
  classChapterIds: string[]
  interestIds: string[]
}

export function photoKindFilters(kind: PhotoKind, groupIds: GroupKindSplit): CrudFilter[] {
  if (kind === 'memories') {
    const ids = groupIds.classChapterIds.length ? groupIds.classChapterIds : [NO_MATCH_SENTINEL]
    return [{ field: 'group_id', operator: 'in', value: ids }]
  }
  if (kind === 'group') {
    const ids = groupIds.interestIds.length ? groupIds.interestIds : [NO_MATCH_SENTINEL]
    return [{ field: 'group_id', operator: 'in', value: ids }]
  }
  if (kind === 'gallery') return [{ field: 'group_id', operator: 'null', value: null }]
  return []
}
